import {
  Compass,
  Lightbulb,
  ListChecks,
  Megaphone,
  MessagesSquare,
  MousePointerClick,
  Sparkles,
  Tag,
  type LucideIcon,
} from "lucide-react";
import type { GoalCategoryId, GoalId } from "./types";
import { goalCardStrings, goalCategoryStrings } from "./strings";

/**
 * Goal config — data, not hardcoded blocks. All 8 goals ship in the UI at
 * once; backend wiring lands in waves and the UI doesn't know or care.
 *
 * Illustrations: every goal has bespoke artwork (goal-art-*.webp) — the
 * conceptual-pun suite: one bold object + one visual pun per decision on a
 * warm cream field, slate-ink shapes, risograph grain, single category
 * accent (emerald/sky/amber), motif staged in the lower 65% so it survives
 * the bottom-anchored object-cover crop at every bento footprint. See
 * docs/plans/goal-card-illustration-suite.md.
 */

export interface GoalIllustration {
  light: string;
  dark: string;
  /**
   * New art series (conceptual-pun suite —
   * docs/plans/goal-card-illustration-suite.md) renders as a full-bleed
   * painted band (object-cover + top fade) instead of a floating vignette.
   * Legacy line-art SVGs leave this unset.
   */
  fullBleed?: boolean;
  /** Optional per-art positioning for full-bleed assets with unusual crop needs. */
  fullBleedClassName?: string;
}

/**
 * Two illustration sets ship simultaneously so the new suite can be A/B'd
 * against the pre-redesign line art via the `?art=legacy` URL param (see
 * resolveGoalIllustration + GoalSelectionStep). Default is the new suite.
 * This is a temporary comparison affordance — drop `legacy` + the param
 * once a set is chosen.
 */
export type GoalIllustrationSet = "art" | "legacy";

export interface GoalDefinition {
  id: GoalId;
  /** Presentational grouping for the goal screen filter. */
  category: GoalCategoryId;
  title: string;
  subtitle: string;
  /** Classic MRX study name shown as the card tag (e.g. "Ad Testing"). */
  methodTag: string;
  /** Lucide glyph that hints the study type in the flow-header GoalChip. */
  icon: LucideIcon;
  /** New conceptual-pun suite (default). */
  illustration: GoalIllustration;
  /** Pre-redesign line art, kept active for comparison (`?art=legacy`). */
  illustrationLegacy: GoalIllustration;
  /**
   * When true the card renders with a brand-tinted background (primary/20)
   * to visually distinguish it as the recommended / featured study type.
   */
  featured?: boolean;
  /**
   * Goal only ships as a full study today; the Quick Study arc for it is not
   * ready. Quick mode renders the card locked with "Coming soon".
   */
  fullStudyOnly?: boolean;
}

export interface GoalCategory {
  id: GoalCategoryId;
  label: string;
}

/** Row order mirrors GOALS grouping: test → brand → explore. */
export const GOAL_CATEGORIES: Array<GoalCategory> = [
  { id: "test", label: goalCategoryStrings.test },
  { id: "brand", label: goalCategoryStrings.brand },
  { id: "explore", label: goalCategoryStrings.explore },
];

/**
 * "One bold object, one visual pun per decision" suite — Comp B direction
 * from docs/plans/goal-card-illustration-suite.md. Day art doubles as the
 * dark variant until the dark batch lands.
 */
const ART_ILLUSTRATIONS = {
  conceptTesting: {
    light: "/illustrations/goal-art-concept-validation.webp",
    dark: "/illustrations/goal-art-concept-validation.webp",
    fullBleed: true,
  },
  adMessaging: {
    light: "/illustrations/goal-art-ad-creative.webp",
    dark: "/illustrations/goal-art-ad-creative.webp",
    fullBleed: true,
    fullBleedClassName: "object-[center_74%]",
  },
  brandPerception: {
    light: "/illustrations/goal-art-brand-perception.webp",
    dark: "/illustrations/goal-art-brand-perception.webp",
    fullBleed: true,
  },
  customerDiscovery: {
    light: "/illustrations/goal-art-explore.webp",
    dark: "/illustrations/goal-art-explore.webp",
    fullBleed: true,
  },
  landingPage: {
    light: "/illustrations/goal-art-lp-conversion.webp",
    dark: "/illustrations/goal-art-lp-conversion.webp",
    fullBleed: true,
  },
  messageTesting: {
    light: "/illustrations/goal-art-claim-validation.webp",
    dark: "/illustrations/goal-art-claim-validation.webp",
    fullBleed: true,
  },
  needsAssessment: {
    light: "/illustrations/goal-art-pain-ranking.webp",
    dark: "/illustrations/goal-art-pain-ranking.webp",
    fullBleed: true,
  },
  pricing: {
    light: "/illustrations/goal-art-pricing-packaging.webp",
    dark: "/illustrations/goal-art-pricing-packaging.webp",
    fullBleed: true,
  },
} as const;

/**
 * Pre-redesign line art — the original set on the cards before the suite.
 * Light gray strokes / fills with a single mint accent, bottom-anchored
 * floating vignette (not full-bleed). Kept active for `?art=legacy` A/B.
 */
const LEGACY_ILLUSTRATIONS = {
  conceptTesting: {
    light: "/illustrations/study-illustration-concept-testing.svg",
    dark: "/illustrations/study-illustration-concept-testing-dark.svg",
  },
  adMessaging: {
    light: "/illustrations/study-illustration-ad-messaging.svg",
    dark: "/illustrations/study-illustration-ad-messaging-dark.svg",
  },
  brandPerception: {
    light: "/illustrations/study-illustration-brand-perception.svg",
    dark: "/illustrations/study-illustration-brand-perception-dark.svg",
  },
  customerDiscovery: {
    light: "/illustrations/study-illustration-customer-discovery.svg",
    dark: "/illustrations/study-illustration-customer-discovery-dark.png",
  },
  landingPage: {
    light: "/illustrations/goal-illustration-landing-page.svg",
    dark: "/illustrations/goal-illustration-landing-page-dark.svg",
  },
  messageTesting: {
    light: "/illustrations/goal-illustration-message-testing.svg",
    dark: "/illustrations/goal-illustration-message-testing-dark.svg",
  },
  needsAssessment: {
    light: "/illustrations/goal-illustration-needs-assessment.svg",
    dark: "/illustrations/goal-illustration-needs-assessment-dark.svg",
  },
  pricing: {
    light: "/illustrations/goal-illustration-pricing.svg",
    dark: "/illustrations/goal-illustration-pricing-dark.svg",
  },
} as const;

/**
 * explore leads as the featured / recommended card. The remaining goals follow
 * the original grouping: test (4) · brand (2) · explore (1, pain_ranking).
 */
export const GOALS: Array<GoalDefinition> = [
  {
    id: "explore",
    category: "explore",
    icon: Compass,
    featured: true,
    ...goalCardStrings.explore,
    illustration: ART_ILLUSTRATIONS.customerDiscovery,
    illustrationLegacy: LEGACY_ILLUSTRATIONS.customerDiscovery,
  },
  {
    id: "lp_conversion",
    category: "test",
    icon: MousePointerClick,
    ...goalCardStrings.lp_conversion,
    illustration: ART_ILLUSTRATIONS.landingPage,
    illustrationLegacy: LEGACY_ILLUSTRATIONS.landingPage,
  },
  {
    id: "ad_creative",
    category: "test",
    icon: Megaphone,
    ...goalCardStrings.ad_creative,
    illustration: ART_ILLUSTRATIONS.adMessaging,
    illustrationLegacy: LEGACY_ILLUSTRATIONS.adMessaging,
  },
  {
    id: "concept_validation",
    category: "test",
    icon: Lightbulb,
    ...goalCardStrings.concept_validation,
    illustration: ART_ILLUSTRATIONS.conceptTesting,
    illustrationLegacy: LEGACY_ILLUSTRATIONS.conceptTesting,
  },
  {
    id: "pricing_packaging",
    category: "test",
    icon: Tag,
    ...goalCardStrings.pricing_packaging,
    illustration: ART_ILLUSTRATIONS.pricing,
    illustrationLegacy: LEGACY_ILLUSTRATIONS.pricing,
    fullStudyOnly: true,
  },
  {
    id: "brand_perception",
    category: "brand",
    icon: Sparkles,
    ...goalCardStrings.brand_perception,
    illustration: ART_ILLUSTRATIONS.brandPerception,
    illustrationLegacy: LEGACY_ILLUSTRATIONS.brandPerception,
    fullStudyOnly: true,
  },
  {
    id: "claim_validation",
    category: "brand",
    icon: MessagesSquare,
    ...goalCardStrings.claim_validation,
    illustration: ART_ILLUSTRATIONS.messageTesting,
    illustrationLegacy: LEGACY_ILLUSTRATIONS.messageTesting,
  },
  {
    id: "pain_ranking",
    // Pains are seeded as concept assets (contract §0).
    category: "explore",
    icon: ListChecks,
    ...goalCardStrings.pain_ranking,
    illustration: ART_ILLUSTRATIONS.needsAssessment,
    illustrationLegacy: LEGACY_ILLUSTRATIONS.needsAssessment,
  },
];

export function getGoalById(goalId: string | null): GoalDefinition | null {
  if (!goalId) return null;
  return GOALS.find((goal) => goal.id === goalId) ?? null;
}

/**
 * Picks which illustration set a card renders. Default is the new suite;
 * `legacy` returns the pre-redesign line art. Temporary A/B affordance —
 * see GoalSelectionStep's `?art=legacy` read.
 */
export function resolveGoalIllustration(
  goal: GoalDefinition,
  set: GoalIllustrationSet,
): GoalIllustration {
  return set === "legacy" ? goal.illustrationLegacy : goal.illustration;
}

export function isGoalIllustrationSet(
  value: string | null | undefined,
): value is GoalIllustrationSet {
  return value === "art" || value === "legacy";
}
